import type { DeepString } from "@/lib/i18n";

/**
 * Paragraph (system-wide) — polskie tłumaczenia.
 *
 * Słowniczek wspólny dla wszystkich layoutów akapitów + komponentów
 * pomocniczych (Attachments, ImageGallery, ContactForm).
 */
export const pl = {
  /** Akapit z layoutem którego renderer nie obsługuje (dev placeholder). */
  unsupportedLayout: (n: number) => `Akapit typu ${n}`,
  unsupportedLayoutBody:
    "— renderer nie obsługuje tego układu. Akapit istnieje w bazie ale jego layout nie został zaimplementowany w komponencie.",

  attachments: {
    /** Sekcja "Załączniki" pod akapitem. */
    heading: "Załączniki",
    /** Aria label przycisku download. */
    download: "Pobierz plik",
    /** Suffix do `name`, np. "Faktura (PDF, 2.345 MB)". */
    sizeMb: (mb: string) => `${mb} MB`,
  },

  gallery: {
    /** Aria label kafelka zdjęcia. */
    openImage: "Otwórz zdjęcie w pełnym widoku",
    /** Aria label dla `<picture>` bez opisu. */
    image: "Zdjęcie",
    /** Komunikat gdy galeria pusta (rzadko — backend nie powinien dać empty). */
    empty: "Brak zdjęć w akapicie.",
  },

  video: {
    /** Title atrybut iframe gdy brak nazwy akapitu. */
    youtubeTitleFallback: "Materiał wideo YouTube",
    /** Komunikat gdy backend nie zwrócił poprawnego URL embed. */
    missing: "Wideo niedostępne (brak źródła).",
  },

  blogPosts: {
    /** Pusta lista postów. */
    empty: "Brak powiązanych wpisów blogowych.",
    /** Aria label kafelka postu (`{name}`). */
    open: (name: string) => `Otwórz wpis: ${name}`,
    /** Suffix "Zobacz na blogu" pod kafelkami. */
    viewMore: "Zobacz więcej",
  },

  services: {
    /** Link "Dowiedz się więcej" pod opisem karty w ParagraphServices. */
    readMore: "Dowiedz się więcej",
  },

  realizationsTestimonials: {
    /** CTA na karcie realizacji. */
    seeRealization: "Zobacz realizację",
    /** Default label "Wszystkie realizacje" CTA. */
    seeAll: "Wszystkie realizacje",
    /** Aria labels dla Swiper testimoniali. */
    prev: "Poprzednia",
    next: "Następna",
    goToSlide: "Przejdź do referencji",
  },

  blogLatest: {
    /** CTA na karcie wpisu blogowego. */
    readMore: "Czytaj dalej",
    /** Default label "Przejdz do bazy wiedzy" gdy admin nie ustawil see_all_label. */
    seeAllDefault: "Przejdź do bazy wiedzy",
    /** Suffix czas czytania (np. "7 min"). */
    minReadShort: "min",
  },

  relatedCatalogs: {
    /** Default label CTA "Dowiedz sie wiecej" gdy admin nie ustawil cta_label. */
    readMore: "Dowiedz się więcej",
  },

  map: {
    /** Komunikat gdy backend nie zwrócił żadnych punktów GPX. */
    empty: "Brak punktów do wyświetlenia na mapie.",
    /** Loading state przed dynamic importem leaflet'u. */
    loading: "Ładowanie mapy…",
  },

  contactForm: {
    /** Nagłówek sekcji formularza (gdy admin nie ustawił `name` akapitu). */
    heading: "Formularz kontaktowy",
    /** Domyślne label'e pól (gdy backend nie nadpisuje). */
    labels: {
      phone: "Telefon",
      email: "E-mail",
      text: "Treść wiadomości",
      personalData: "Zgadzam się na przetwarzanie danych osobowych",
    },
    /** Domyślny tekst zgody RODO (gdy backend nie ustawił). */
    rodoDefault:
      "Wyrażam zgodę na przetwarzanie moich danych osobowych w celu obsługi zapytania.",
    submit: "Wyślij wiadomość",
    sending: "Wysyłanie…",
    /** Komunikaty walidacyjne (client-side). */
    validation: {
      requiredField: (label: string) => `Pole „${label}" jest wymagane.`,
      invalidEmail: "Podaj poprawny adres e-mail.",
      invalidPhone: "Podaj poprawny numer telefonu.",
      requiredPersonalData: "Wymagana zgoda na przetwarzanie danych osobowych.",
    },
    /** Komunikaty status (banner / toast). */
    status: {
      sendSuccess: "Wiadomość została wysłana. Dziękujemy za kontakt.",
      sendFailed:
        "Nie udało się wysłać wiadomości. Spróbuj ponownie za chwilę.",
      rateLimit:
        "Zbyt wiele prób z tego adresu IP. Odczekaj chwilę przed kolejną próbą.",
      tokenExpired:
        "Sesja formularza wygasła. Odśwież stronę i spróbuj ponownie.",
      antiSpam:
        "Wykryto podejrzaną aktywność. Sprawdź pola formularza i spróbuj ponownie.",
      waitBeforeSubmit: (sec: number) =>
        `Odczekaj jeszcze ${sec} s przed wysłaniem formularza.`,
      networkError: (msg: string) => `Błąd sieci: ${msg}.`,
      networkErrorGeneric: "Nie udało się połączyć z serwerem. Spróbuj ponownie.",
    },
  },

  heroCta: {
    /** Aria label stosu kart realizacji w akapicie hero (układ 85). */
    realizationsAria: "Wybrane realizacje",
    /** Aria label linku karty, gdy realizacja nie ma nazwy. */
    openRealization: "Zobacz realizację",
  },
} as const;

export type ParagraphTranslations = DeepString<typeof pl>;
